const isObject = (item: unknown): boolean => typeof item === 'object' && !Array.isArray(item) && item !== null;
const isArray = (item: unknown): boolean => typeof item === 'object' && Array.isArray(item);
const isUndefined = (item: unknown): boolean => typeof item === 'undefined'

type MergeDeep = <T extends Record<string, unknown> | unknown[]>(target: T, source: T, options?: { skipUndefined?: boolean }) => T

const mergeDeep: MergeDeep = (target, source, options) => {
  // Merge arrays (index-wise). If both items at an index are objects/arrays, they are merged recursively.
  if (isArray(target) && isArray(source)) {
    const t = target as any[];
    const s = source as any[];
    const maxLen = Math.max(t.length, s.length);
    const result: any[] = [];

    for (let i = 0; i < maxLen; i++) {
      const tVal = t[i];
      const sVal = s[i];

      // Both are objects or both are arrays -> merge recursively
      if ((isObject(tVal) && isObject(sVal)) || (isArray(tVal) && isArray(sVal))) {
        result[i] = mergeDeep(tVal, sVal, options);
      } else if (isUndefined(sVal) && options?.skipUndefined) {
        // keep target value when source explicitly undefined and skipUndefined is true
        result[i] = tVal;
      } else if (!(i in s)) {
        // source doesn't have this index -> keep target
        result[i] = tVal;
      } else {
        // use source value (could be undefined if skipUndefined is false)
        result[i] = sVal;
      }
    }

    return result;
  }

  // Merge plain objects
  if (isObject(target) && isObject(source)) {
    const output = Object.assign({}, target) as any;

    Object.keys(source).forEach(key => {
      const sourceValue = (source as Record<string, any>)[key];

      // If source value is an object or array, merge recursively (if key exists) or assign directly
      if (isObject(sourceValue) || isArray(sourceValue)) {
        if (!(key in target))
          Object.assign(output, { [key]: sourceValue });
        else
          output[key] = mergeDeep((target as Record<string, any>)[key], sourceValue, options);

      } else if (isUndefined(sourceValue) && options?.skipUndefined) {
        return;
      } else {
        Object.assign(output, { [key]: sourceValue });
      }
    });

    return output;
  }

  // Fallback: if source is undefined and skipUndefined is true, keep target; otherwise use source
  if (isUndefined(source) && options?.skipUndefined) return target;
  return source;
}

export default mergeDeep;
